import { isRejectedWithValue } from '@reduxjs/toolkit';

import { ExtraReducerBuilder } from '../../types/shared';
import { WritableQuoteBuilderStateType } from '../../types/state';
import { isFormDirty } from '../selectors/quote-builder-dirty';
import { createQuoteLineOptimistically } from '../thunks/create-quote-line-optimistically';
import { deleteIssue } from '../utilities/issue-managers';
import { stringifyQuote } from '../utilities/stringify-quote';
import { transformSnapQuoteLineToStoreState } from '../utilities/transform-quote-line';

type CreateQuoteLineOptimisticallyFulfilledAction = ReturnType<
  typeof createQuoteLineOptimistically.fulfilled
>;

type CreateQuoteLineOptimisticallyRejectedAction = ReturnType<
  typeof createQuoteLineOptimistically.rejected
>;

function removeOptimisticQuoteLine(
  state: WritableQuoteBuilderStateType,
  optimisticUuid: string,
) {
  Object.entries(state.issues).forEach(([issueId, issue]) => {
    if (issue.area === 'quoteLine' && issue.quoteLineUuid === optimisticUuid) {
      deleteIssue(state, issueId);
    }
  });

  state.quoteLineUuids = state.quoteLineUuids.filter(
    (id) => id !== optimisticUuid,
  );
  delete state.quoteLines[optimisticUuid];
}

function createQuoteLineOptimisticallyFulfilledBase(
  state: WritableQuoteBuilderStateType,
  action: CreateQuoteLineOptimisticallyFulfilledAction,
) {
  const wasFormDirty = isFormDirty(state);

  removeOptimisticQuoteLine(state, action.meta.optimisticUuid);

  state.pendingServerData = null;

  const { quoteLine, uuid } = transformSnapQuoteLineToStoreState(
    action.payload,
    state,
  );

  const { lineNumber } = quoteLine;

  state.quoteLineUuids.splice(lineNumber - 1, 0, uuid);
  state.quoteLines[uuid] = quoteLine;

  if (!wasFormDirty) {
    state.lastSaveDateTime = new Date();
    state.currentServerData = stringifyQuote(state);
  }
}

function createQuoteLineOptimisticallyRejected(
  state: WritableQuoteBuilderStateType,
  action: CreateQuoteLineOptimisticallyRejectedAction,
) {
  if (!action.meta.optimisticUuid) {
    return;
  }

  removeOptimisticQuoteLine(state, action.meta.optimisticUuid);

  state.pendingServerData = null;
}

export function createQuoteLineOptimisticallyReducer(
  builder: ExtraReducerBuilder,
) {
  builder.addMatcher(
    createQuoteLineOptimistically.fulfilled.match,
    createQuoteLineOptimisticallyFulfilledBase,
  );

  builder.addMatcher(
    isRejectedWithValue(createQuoteLineOptimistically),
    createQuoteLineOptimisticallyRejected,
  );
}
